import {
  PaymentMethodsResponse,
  CreateOrderRequest,
  CreateOrderResponse,
  ProcessPaymentRequest,
  ProcessPaymentResponse,
  PaymentOrder
} from '@/types'

// API 基础配置
const API_BASE_URL = (import.meta as any).env?.VITE_API_BASE_URL || 'https://vissynth-backend-2nas2n7wxa-uc.a.run.app'

// 获取请求头
const getHeaders = () => {
  const token = localStorage.getItem('token') || localStorage.getItem('auth_token') || ''
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  }
}

// 通用请求处理
const handleResponse = async <T>(response: Response): Promise<T> => {
  if (!response.ok) {
    const errorText = await response.text()
    throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`)
  }

  const data = await response.json()
  console.log('📥 [handleResponse] Full API response:', data)

  // 根据API文档格式处理响应
  if (data.code !== 200) {
    throw new Error(data.message || 'API request failed')
  }

  // 对于ProcessPaymentResponse，我们需要包含status字段
  if (data.data.status !== undefined) {
    console.log('✅ [handleResponse] Payment status:', data.data.status)
  }

  return data.data as T
}

/**
 * 获取支持的支付方式
 */
export const getPaymentMethods = async (): Promise<PaymentMethodsResponse> => {
  const response = await fetch(`${API_BASE_URL}/payments/methods`, {
    method: 'GET',
    headers: getHeaders()
  })

  return handleResponse<PaymentMethodsResponse>(response)
}

/**
 * 创建支付订单
 */
export const createPaymentOrder = async (request: CreateOrderRequest): Promise<CreateOrderResponse> => {
  const headers = getHeaders()
  console.log('🔑 [createPaymentOrder] Request headers:', headers)
  console.log('📝 [createPaymentOrder] Request body:', request)
  console.log('🌐 [createPaymentOrder] API URL:', `${API_BASE_URL}/api/payments/orders`)

  const response = await fetch(`${API_BASE_URL}/payments/orders`, {
    method: 'POST',
    headers: headers,
    body: JSON.stringify(request)
  })

  console.log('📡 [createPaymentOrder] Response status:', response.status, response.statusText)

  return handleResponse<CreateOrderResponse>(response)
}

/**
 * 处理支付
 */
export const processPayment = async (request: ProcessPaymentRequest): Promise<ProcessPaymentResponse> => {
  const headers = getHeaders()
  console.log('🔑 [processPayment] Request headers:', headers)
  console.log('📝 [processPayment] Request body:', request)
  console.log('🌐 [processPayment] API URL:', `${API_BASE_URL}/api/payments/process`)

  const response = await fetch(`${API_BASE_URL}/payments/process`, {
    method: 'POST',
    headers: headers,
    body: JSON.stringify(request)
  })

  console.log('📡 [processPayment] Response status:', response.status, response.statusText)

  return handleResponse<ProcessPaymentResponse>(response)
}

/**
 * 查询订单状态
 */
export const getOrderStatus = async (orderId: number): Promise<PaymentOrder> => {
  const response = await fetch(`${API_BASE_URL}/payments/orders/${orderId}`, {
    method: 'GET',
    headers: getHeaders()
  })

  return handleResponse<PaymentOrder>(response)
}

// 删除了非订阅订单的支付处理方法，因为API现在只支持订阅订单

/**
 * 升级订阅
 */
export const upgradeSubscription = async (
  subscriptionType: number,
  billingCycle: number = 1 // 1=月付, 12=年付
): Promise<any> => {
  try {
    const response = await fetch(`${API_BASE_URL}/subscriptions/upgrade`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({
        subscription_type: subscriptionType,
        billing_cycle: billingCycle
      })
    });

    return handleResponse<any>(response);
  } catch (error) {
    console.error('Subscription upgrade failed:', error);
    throw error;
  }
};

/**
 * 处理PayPal订阅支付
 * 便捷方法，整合创建订阅订单和处理支付
 */
export const processPayPalSubscription = async (
  subscriptionType: number,
  billingCycle: number = 1 // 1=月付, 12=年付
): Promise<ProcessPaymentResponse> => {
  try {
    // 1. 创建订阅订单
    const order = await createPaymentOrder({
      subscription_type: subscriptionType,
      billing_cycle: billingCycle
    })

    // 2. 处理PayPal支付
    const paymentResult = await processPayment({
      order_id: order.order_id,
      payment_method: 'paypal'
    })

    return paymentResult
  } catch (error) {
    console.error('PayPal subscription processing failed:', error)
    throw error
  }
};

/**
 * 处理PayPal支付 - 明确区分新订阅和升级订阅
 * 升级订阅流程：先调用升级接口创建升级订阅，再调用支付接口完成支付
 * 新订阅流程：直接调用创建订单和支付接口
 */
export const processPayPalPayment = async (
  isUpgrade: boolean,
  subscriptionType: number,
  billingCycle: number = 1, // 1=月付, 12=年付
  upgradeFrom?: number, // 当前订阅类型，仅升级时需要
  upgradePrice?: number // 升级价格，仅升级时需要
): Promise<ProcessPaymentResponse> => {
  try {
    let order;

    if (isUpgrade && upgradeFrom && upgradeFrom !== 0) {
      // 升级订阅流程 Step 1: 调用升级接口创建升级订阅
      const upgradeResult = await upgradeSubscription(subscriptionType, billingCycle);

      // Step 2: 使用升级结果创建支付订单
      order = await createPaymentOrder({
        subscription_type: subscriptionType,
        billing_cycle: billingCycle,
        is_upgrade: true,
        upgrade_from: upgradeFrom,
        upgrade_price: upgradePrice // 传递升级价格
      });
    } else {
      // 新订阅流程：直接创建订阅订单
      order = await createPaymentOrder({
        subscription_type: subscriptionType,
        billing_cycle: billingCycle,
        is_upgrade: false
      });
    }

    // 统一支付流程：处理PayPal支付
    const paymentResult = await processPayment({
      order_id: order.order_id,
      payment_method: 'paypal'
    });

    return paymentResult;
  } catch (error) {
    console.error('PayPal payment processing failed:', error);
    throw error;
  }
};

/**
 * 轮询订单支付状态
 */
export const pollOrderStatus = (
  orderId: number,
  onSuccess: (order: PaymentOrder) => void,
  onError: (error: Error) => void,
  interval: number = 3000, // 3秒轮询一次
  maxAttempts: number = 20 // 最多轮询20次（1分钟）
): NodeJS.Timeout => {
  let attempts = 0

  const poll = async () => {
    try {
      attempts++
      const order = await getOrderStatus(orderId)

      // 检查支付状态
      if (order.payment_status === 3) { // 支付成功
        onSuccess(order)
        return
      }

      if (order.payment_status === 4) { // 支付失败
        onError(new Error('Payment failed'))
        return
      }

      // 如果还在处理中，继续轮询
      if (attempts < maxAttempts) {
        setTimeout(poll, interval)
      } else {
        onError(new Error('Payment timeout'))
      }
    } catch (error) {
      onError(error as Error)
    }
  }

  return setTimeout(poll, interval)
}

/**
 * 获取当前会员状态和权益汇总
 */
export const getMembershipStatus = async (): Promise<any> => {
  const response = await fetch(`${API_BASE_URL}/benefits/summary`, {
    method: 'GET',
    headers: getHeaders()
  })

  return handleResponse(response)
}